import { z } from 'zod';

/**
 * Mirrors apps/api/src/modules/promotion/http/coupon.dto.ts's
 * `couponInputSchema` — same two business-rule `.refine()`s, same field
 * set. Two differences, both purely about HTML form ergonomics, neither a
 * business rule of their own:
 *
 * - `validFrom`/`validUntil` are plain strings here, the raw value of a
 *   native `<input type="datetime-local">` ('' when empty) — converted to
 *   an ISO datetime string (or `null`) only at submit time, in `api.ts`'s
 *   `couponToBody()`, the same place `ProductForm`'s `productToBody()`
 *   already does its own form-to-API mapping.
 * - `minOrderAmount`/`usageLimitTotal`/`usageLimitPerCustomer` are typed
 *   `number | null` (never `undefined`) because each field is wired with
 *   RHF's `setValueAs` to turn an emptied input into `null` directly —
 *   avoids the `NaN` a bare `valueAsNumber: true` would produce on ''.
 */
export const couponFormSchema = z
  .object({
    code: z.string().trim().min(1, 'Le code est requis.').max(50),
    type: z.enum(['fixed', 'percentage']),
    value: z.coerce
      .number({ invalid_type_error: 'La valeur est requise.' })
      .positive('La valeur doit être positive.'),
    minOrderAmount: z.number().nonnegative('Doit être positif ou nul.').nullable(),
    usageLimitTotal: z.number().int().positive('Doit être un entier positif.').nullable(),
    usageLimitPerCustomer: z.number().int().positive('Doit être un entier positif.').nullable(),
    validFrom: z.string(),
    validUntil: z.string(),
    isActive: z.boolean(),
  })
  .refine((data) => data.type !== 'percentage' || data.value <= 100, {
    message: 'Une remise en pourcentage ne peut pas dépasser 100.',
    path: ['value'],
  })
  .refine(
    (data) =>
      data.validFrom === '' ||
      data.validUntil === '' ||
      new Date(data.validFrom) < new Date(data.validUntil),
    { message: 'La date de début doit précéder la date de fin.', path: ['validUntil'] },
  );

export type CouponFormValues = z.infer<typeof couponFormSchema>;
